import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, FileText, Users, ShoppingCart, MessageCircle, LogOut, Menu, X, Bell, Eye } from 'lucide-react';
import { Logo } from './Logo';
import { adminApi } from '../lib/api';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, testid: 'nav-dashboard' },
  { to: '/admin/inventory', label: 'Inventory', icon: Package, testid: 'nav-inventory' },
  { to: '/admin/orders', label: 'Orders', icon: FileText, testid: 'nav-orders' },
  { to: '/admin/customers', label: 'Customers', icon: Users, testid: 'nav-customers' },
  { to: '/admin/requests', label: 'Requests', icon: ShoppingCart, testid: 'nav-requests' },
  { to: '/admin/messages', label: 'Messages', icon: MessageCircle, testid: 'nav-messages' },
  { to: '/admin/samples', label: 'Samples', icon: Eye, testid: 'nav-samples' },
];

// 5 items shown in the mobile bottom-nav bar (rest accessible via hamburger menu)
const bottomNavItems = navItems.filter((n) =>
  ['/admin', '/admin/inventory', '/admin/orders', '/admin/customers', '/admin/messages'].includes(n.to),
);

export default function AdminLayout({ children }) {
  const [open, setOpen] = useState(false);
  const [badges, setBadges] = useState({ pending_orders: 0, unread_messages: 0 });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await adminApi.get('/admin/dashboard');
        setBadges({
          pending_orders: r.data.pending_orders || 0,
          unread_messages: r.data.unread_messages || 0,
        });
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const logout = () => { localStorage.removeItem('adminToken'); navigate('/admin/login'); };
  const badgeFor = (to) => {
    if (to === '/admin/requests') return badges.pending_orders;
    if (to === '/admin/messages') return badges.unread_messages;
    return 0;
  };

  return (
    <div className="min-h-screen bg-[#fafafa] pb-16 lg:pb-0">
      {/* Top bar (mobile) */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#e7e7e7]">
        <div className="flex items-center justify-between px-4 h-24">
          <Link to="/admin"><Logo size="md" /></Link>
          <button onClick={() => setOpen(!open)} className="p-2 -mr-2" data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed lg:sticky top-0 left-0 h-screen z-50 w-72 bg-white border-r border-[#e7e7e7] transition-transform duration-200 ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 flex flex-col`}>
          <div className="p-6 pb-4 hidden lg:block">
            <Link to="/admin"><Logo size="xl" /></Link>
            <div className="text-xs text-[#6b7280] mt-3 font-medium uppercase tracking-wider">Admin Console</div>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map(({ to, label, icon: Icon, end, testid }) => {
              const badge = badgeFor(to);
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? 'bg-[#fde8e8] text-[#d92027]' : 'text-[#1f1f1f] hover:bg-[#fafafa]'
                    }`
                  }
                >
                  <span className="flex items-center gap-3">
                    <Icon size={18} />
                    {label}
                  </span>
                  {badge > 0 && (
                    <span className="bg-[#d92027] text-white text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] rounded-full flex items-center justify-center">
                      {badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>
          <div className="p-3 border-t border-[#e7e7e7]">
            <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#6b7280] hover:bg-[#fafafa]" data-testid="admin-logout-btn">
              <LogOut size={18} /> Sign out
            </button>
          </div>
        </aside>

        {open && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setOpen(false)} />}

        <main className="flex-1 min-w-0 min-h-screen">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            {children}
          </div>
        </main>
      </div>

      {/* Bottom nav (mobile only) — 5 most-used screens; rest live in hamburger menu */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#e7e7e7] shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
        data-testid="admin-bottom-nav"
      >
        <div className="grid grid-cols-5">
          {bottomNavItems.map(({ to, label, icon: Icon, end, testid }) => {
            const badge = badgeFor(to);
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`bottom-${testid}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition-colors ${
                    isActive ? 'text-[#d92027]' : 'text-[#6b7280]'
                  }`
                }
              >
                <div className="relative">
                  <Icon size={20} />
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 bg-[#d92027] text-white text-[9px] font-bold px-1 min-w-[16px] h-[16px] rounded-full flex items-center justify-center">
                      {badge}
                    </span>
                  )}
                </div>
                {label}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
